import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router";
import { Banknote, CheckCircle2, Clock, ExternalLink, FileCheck2, Landmark, PackageX, Truck, Upload } from "lucide-react";
import { orderApi, type OrderDetail } from "../../api/cart";
import { ApiError } from "../../api/client";
import { OrderStatusBadge } from "../../components/cart/OrderStatusBadge";
import { quantityUnit } from "../../components/cart/units";
import { LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Button, Card, Dialog, Icon } from "../../components/ui";
import { formatDate, formatDay, formatMoment } from "../../lib/dates";
import { formatPesos } from "../../lib/money";
import { errorMessage, useCustomerSession } from "../account/session";
import { NotFound } from "../catalogue/NotFoundRoute";
import { usePageTitle } from "../catalogue/usePageTitle";
import { ORDERS_KEY } from "./OrdersRoute";
import { SignedInOnly } from "./SignedInOnly";

/**
 * PRD screens 10 and 12: straight after placing, the confirmation; afterwards, where the order
 * is. Either way, while payment is awaited: where to send it and the upload for the receipt.
 */
export function OrderRoute() {
  const reference = useParams().reference ?? "";
  usePageTitle(`Order ${reference}`);
  return (
    <SignedInOnly>
      <OrderLoader reference={reference} />
    </SignedInOnly>
  );
}

function OrderLoader({ reference }: { reference: string }) {
  const order = useQuery({ queryKey: [...ORDERS_KEY, reference], queryFn: () => orderApi.get(reference) });
  const placed = (useLocation().state as { placed?: boolean } | null)?.placed ?? false;
  if (order.isPending) return <PageWidth className="py-10 type-body text-text-muted">Loading…</PageWidth>;
  if (order.isError) {
    if (order.error instanceof ApiError && order.error.status === 404)
      return (
        <NotFound
          pageTitle="Order not found"
          icon={PackageX}
          title="We couldn't find that order"
          action={<Link to="/orders">See all your orders</Link>}
        >
          Check the link or reference number, or see all your orders.
        </NotFound>
      );
    return (
      <PageWidth className="py-10">
        <LoadError onRetry={() => order.refetch()} />
      </PageWidth>
    );
  }
  return <Order order={order.data} placed={placed} />;
}

function Order({ order, placed }: { order: OrderDetail; placed: boolean }) {
  // An account without an email gets no order emails; staff text it instead (PRD 5.1).
  const emailed = Boolean(useCustomerSession().data?.user?.email_address);
  return (
    <PageWidth className="max-w-[960px] pt-8">
      {placed && (
        <div role="status" className="mb-6 flex items-start gap-3 rounded-md border border-green-200 bg-success-100 p-4">
          <Icon icon={CheckCircle2} size={22} className="mt-0.5 text-success-700" />
          <div>
            <p className="mb-1 type-body-strong text-success-700">Order placed. Thank you.</p>
            <p className="mb-0 type-small text-text-body">{placedNote(order, emailed)}</p>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="m-0 type-h1 [font-size:var(--text-2xl)]">
          Order <span className="type-sku [font-size:var(--text-xl)] text-text-strong">{order.reference}</span>
        </h1>
        <OrderStatusBadge order={order} />
        <span className="w-full type-small text-text-muted">Placed {formatDay(order.placed_at)}</span>
      </div>

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-8">
          {order.bank_details && (order.awaiting_quote ? <AwaitingQuote order={order} emailed={emailed} /> : <HowToPay order={order} bank={order.bank_details} emailed={emailed} />)}
          {order.payment_method === "cash_on_delivery" && ["confirmed", "packed", "dispatched"].includes(order.status) && <PayOnDelivery order={order} emailed={emailed} />}
          <Lines order={order} />
        </div>
        <div className="flex flex-col gap-4">
          {order.tracking && <Tracking tracking={order.tracking} />}
          <Where order={order} />
          {order.note && (
            <Card padding="var(--space-5)">
              <Label>Your note</Label>
              <p className="m-0 type-body whitespace-pre-line text-text-body">{order.note}</p>
            </Card>
          )}
          {order.can_cancel && <CancelOrder order={order} />}
          <Link to="/orders" className="type-label">
            All your orders
          </Link>
        </div>
      </div>
    </PageWidth>
  );
}

function placedNote(order: OrderDetail, emailed: boolean) {
  if (!emailed) {
    if (order.payment_method === "cash_on_delivery") {
      return order.awaiting_quote ? "We'll text you the delivery charge, which you pay with the parts on delivery." : "We'll text you when it's on its way.";
    }
    return order.awaiting_quote ? "We'll text you the delivery charge: then pay the full total by bank transfer." : "Pay by bank transfer below to get it moving.";
  }
  if (order.payment_method === "cash_on_delivery") {
    return order.awaiting_quote ? "We've emailed the order to you. We'll email the delivery charge next, which you pay with the parts on delivery." : "We've emailed the order to you.";
  }
  return order.awaiting_quote
    ? "We've emailed the order to you. We'll email the delivery charge next: then pay the full total by bank transfer."
    : "We've emailed the order and our bank details to you. Pay by bank transfer below to get it moving.";
}

/** A bank transfer is one payment of the full total, so it waits for our delivery quote (design doc 7.1). */
function AwaitingQuote({ order, emailed }: { order: OrderDetail; emailed: boolean }) {
  return (
    <Card padding="var(--space-6)" className="border-gold-300">
      <h2 className="mb-1 flex items-center gap-2 type-h3 [font-size:var(--text-lg)]">
        <Icon icon={Clock} size={20} className="text-gold-700" /> Waiting for our delivery charge
      </h2>
      <p className="mb-0 type-body text-text-body">
        We're working out the delivery{order.fulfilment === "terminal_pickup" ? ", bus fee included" : ""}, and will {emailed ? "email" : "text"} you the full total. Then pay it by bank transfer
        in one payment, and upload the receipt here. Please don't transfer anything before then.
      </p>
    </Card>
  );
}

/** What the customer hands over, and to whom (PRD 5.9). */
function PayOnDelivery({ order, emailed }: { order: OrderDetail; emailed: boolean }) {
  const to = order.fulfilment === "pickup" ? "at our counter when you collect it" : order.fulfilment === "urgent_delivery" ? "to the rider when it arrives" : "to the courier when it arrives";
  return (
    <Card padding="var(--space-6)" className="border-gold-300">
      <h2 className="mb-1 flex items-center gap-2 type-h3 [font-size:var(--text-lg)]">
        <Icon icon={Banknote} size={20} className="text-gold-700" /> {order.fulfilment === "pickup" ? "Pay when you collect" : "Cash on delivery"}
      </h2>
      {order.awaiting_quote ? (
        <p className="mb-0 type-body text-text-body">
          We'll {emailed ? "email" : "text"} you the delivery charge. Then you pay {formatPesos(order.subtotal_cents)} for the parts, plus that charge, {to}. If you don't agree to it, cancel the
          order here before we pack it.
        </p>
      ) : (
        <p className="mb-0 type-body text-text-body">
          Pay <span className="type-price text-text-strong">{formatPesos(order.total_cents)}</span> {to}
          {order.fulfilment === "urgent_delivery" && ", plus the rider's own fee"}.
        </p>
      )}
    </Card>
  );
}

/** Where to send the money and the upload for the receipt (PRD 5.9). Placing never waited on the upload. */
function HowToPay({ order, bank, emailed }: { order: OrderDetail; bank: NonNullable<OrderDetail["bank_details"]>; emailed: boolean }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [justSent, setJustSent] = useState(false);
  const upload = useMutation({
    mutationFn: (file: File) => orderApi.uploadProof(order.reference, file),
    onSuccess: (next) => {
      queryClient.setQueryData([...ORDERS_KEY, order.reference], next);
      setJustSent(true);
    },
  });
  const proofs = order.payment_proofs;

  return (
    <Card padding="var(--space-6)" className="border-gold-300">
      <h2 className="mb-1 flex items-center gap-2 type-h3 [font-size:var(--text-lg)]">
        <Icon icon={Landmark} size={20} className="text-gold-700" /> Pay by bank transfer
      </h2>
      <p className="mb-5 type-small text-text-muted">We pack your order once we've checked the payment.</p>

      <dl className="m-0 grid gap-x-6 gap-y-3 rounded-md bg-surface-sunken p-4 sm:grid-cols-[auto_minmax(0,1fr)]">
        <Row label="Amount" value={formatPesos(order.total_cents)} strong />
        <Row label="Bank" value={bank.bank_name} />
        <Row label="Account name" value={bank.account_name} />
        <Row label="Account number" value={bank.account_number} mono />
        <Row label="Reference" value={order.reference} mono />
      </dl>
      <p className="mt-3 mb-0 type-small text-text-muted">Put the reference on the transfer so we can match it to your order.</p>

      <div className="mt-6 border-t border-border-subtle pt-5">
        <h3 className="mb-1 type-h4">Then send us the receipt</h3>
        <p className="mb-4 type-small text-text-muted">A photo or screenshot of the transfer, or a PDF, up to 10 MB.</p>
        {proofs.length > 0 && (
          <ul className="m-0 mb-4 flex list-none flex-col gap-2 p-0">
            {proofs.map((proof) => (
              <li key={proof.id} className="flex items-center gap-2 type-small text-text-body">
                <Icon icon={FileCheck2} size={16} className="flex-none text-success-700" />
                <span className="min-w-0 truncate">{proof.filename}</span>
                <span className="flex-none text-text-muted">· {formatMoment(proof.uploaded_at)}</span>
              </li>
            ))}
          </ul>
        )}
        {justSent && !upload.isPending && (
          <p role="status" className="mb-4 type-small text-success-700">
            {emailed ? "Received. We'll check it and let you know by email." : "Received. We'll check it, and this order shows when it's confirmed."}
          </p>
        )}
        <input
          ref={input}
          type="file"
          accept="image/*,application/pdf"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setJustSent(false);
            upload.mutate(file);
          }}
        />
        <Button variant={proofs.length > 0 ? "outline" : "primary"} icon={Upload} disabled={upload.isPending} onClick={() => input.current?.click()}>
          {upload.isPending ? "Uploading…" : proofs.length > 0 ? "Upload another" : "Upload the receipt"}
        </Button>
        {upload.error && (
          <p role="alert" className="mt-3 mb-0 type-small text-danger-700">
            {errorMessage(upload.error)} You can try again, from here or later from this order's page.
          </p>
        )}
      </div>
    </Card>
  );
}

/** Until the order is packed; after that the button is gone and the server refuses anyway (PRD 5.10). */
function CancelOrder({ order }: { order: OrderDetail }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const cancel = useMutation({
    mutationFn: () => orderApi.cancel(order.reference),
    onSuccess: (next) => {
      queryClient.setQueryData([...ORDERS_KEY, order.reference], next);
      queryClient.invalidateQueries({ queryKey: ORDERS_KEY, exact: true });
      setOpen(false);
    },
  });
  const paid = order.payment_method === "bank_transfer" && order.status !== "pending_payment";
  return (
    <>
      <Button variant="quiet" onClick={() => { cancel.reset(); setOpen(true); }} className="self-start">
        Cancel this order
      </Button>
      <Dialog
        open={open}
        title={`Cancel ${order.reference}?`}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="quiet" onClick={() => setOpen(false)}>
              Keep it
            </Button>
            <Button variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              {cancel.isPending ? "Cancelling…" : "Cancel order"}
            </Button>
          </>
        }
      >
        <p className="mb-0 type-body">
          {paid
            ? "We've received your payment. We'll contact you to return it."
            : order.payment_method === "cash_on_delivery"
              ? "You haven't paid anything: it's cash on delivery."
              : "Don't send the payment once it's cancelled."}
        </p>
        {cancel.error && (
          <p role="alert" className="mt-3 mb-0 type-small text-danger-700">
            {errorMessage(cancel.error)}
          </p>
        )}
      </Dialog>
    </>
  );
}

function Row({ label, value, mono, strong }: { label: string; value: string; mono?: boolean; strong?: boolean }) {
  return (
    <div className="contents">
      <dt className="type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase sm:pt-0.5">{label}</dt>
      <dd className={`m-0 -mt-2 select-all sm:mt-0 ${strong ? "type-price [font-size:var(--text-lg)] text-text-strong" : mono ? "type-sku text-text-strong" : "type-body text-text-strong"}`}>
        {value}
      </dd>
    </div>
  );
}

/** What was ordered, at the prices then: the order's own copy, never today's listing (design doc §2.5). */
function Lines({ order }: { order: OrderDetail }) {
  return (
    <section>
      <h2 className="mb-3 type-h3 [font-size:var(--text-lg)]">What you ordered</h2>
      <ul className="m-0 flex list-none flex-col rounded-md border border-border-subtle bg-surface-card p-0">
        {order.items.map((item) => (
          <li key={item.id} className="flex items-start gap-4 border-b border-border-subtle p-4 last:border-b-0">
            <span className="min-w-0 flex-1">
              <span className="block type-sku [font-size:var(--text-xs)] text-ink-600">{item.manufacturer_number ?? item.sku}</span>
              <span className="block type-body text-text-strong">{item.name}</span>
              <span className="block type-small text-text-muted">
                {[item.dimension, `${item.quantity} ${quantityUnit(item.unit, item.quantity)} × ${formatPesos(item.unit_price_cents)}`].filter(Boolean).join(" · ")}
              </span>
            </span>
            <span className="type-price [font-size:var(--text-base)] text-text-strong">{formatPesos(item.line_total_cents)}</span>
          </li>
        ))}
      </ul>
      <dl className="m-0 mt-4 ml-auto flex max-w-[340px] flex-col gap-2 type-body">
        <div className="flex justify-between gap-4">
          <dt>Parts</dt>
          <dd className="m-0 type-sku">{formatPesos(order.subtotal_cents)}</dd>
        </div>
        <div className="flex justify-between gap-4 text-text-muted">
          <dt>{order.fulfilment === "pickup" ? "Pickup" : "Delivery"}</dt>
          <dd className="m-0 type-small">
            {order.delivery_cents !== null
              ? formatPesos(order.delivery_cents)
              : order.fulfilment === "pickup"
                ? "You collect it"
                : order.fulfilment === "urgent_delivery"
                  ? "Paid to the rider"
                  : "Quoted separately"}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4 border-t border-border-subtle pt-2">
          <dt className="type-body-strong">Total</dt>
          <dd className="m-0 type-price [font-size:var(--text-lg)] text-text-strong">{formatPesos(order.total_cents)}</dd>
        </div>
        <dd className="m-0 type-small text-text-muted">Prices include tax.</dd>
      </dl>
    </section>
  );
}

/** Who's bringing it and how to follow it (PRD 5.8). Lalamove and Grab give a link, not a number. */
function Tracking({ tracking }: { tracking: NonNullable<OrderDetail["tracking"]> }) {
  const shareLink = tracking.url === tracking.reference;
  return (
    <Card padding="var(--space-5)" className="border-green-200">
      <Label>On its way with</Label>
      <p className="m-0 flex items-center gap-2 type-body-strong text-text-strong">
        <Icon icon={Truck} size={18} className="text-green-700" /> {tracking.courier_name}
      </p>
      {!shareLink && (
        <p className="mt-2 mb-0 type-small text-text-muted">
          Tracking number <span className="block type-sku [font-size:var(--text-base)] break-all text-text-strong select-all">{tracking.reference}</span>
        </p>
      )}
      {tracking.url && (
        <a href={tracking.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 type-label">
          {shareLink ? "Follow your parcel" : `Track it on the ${tracking.courier_name} site`} <Icon icon={ExternalLink} size={14} />
        </a>
      )}
    </Card>
  );
}

function Where({ order }: { order: OrderDetail }) {
  const { contact, delivery_address: address, pickup_point: pickup, terminal } = order;
  return (
    <Card padding="var(--space-5)">
      <Label>{address ? "Delivering to" : "Collecting from"}</Label>
      {address ? (
        <address className="type-body not-italic text-text-body">
          <span className="block type-body-strong text-text-strong">{contact.recipient_name}</span>
          {address.street}, {address.barangay}
          <br />
          {address.city}, {address.province}
          {address.postal_code && ` ${address.postal_code}`}
          <span className="mt-1 block type-sku [font-size:var(--text-sm)] text-ink-600">{contact.phone}</span>
          {order.fulfilment === "urgent_delivery" && ["pending_payment", "paid", "packed"].includes(order.status) && (
            <span className="mt-3 block type-small text-text-muted">
              Urgent delivery.{" "}
              {order.urgent_due_on ? (
                <>
                  Goes out <span className="type-body-strong [font-size:inherit] text-text-strong">{formatDate(order.urgent_due_on)}</span>. You pay the rider when it arrives.
                </>
              ) : (
                "Goes out the day we confirm your payment if that's before 3 PM, Monday to Saturday, otherwise the next working day."
              )}
            </span>
          )}
        </address>
      ) : (
        <div className="type-body text-text-body">
          {pickup && (
            <p className="mb-2">
              {pickup.address}
              <span className="block type-small text-text-muted">{pickup.hours}</span>
            </p>
          )}
          {terminal && (
            <p className="mb-2">
              {terminal.terminal}
              <span className="block type-small text-text-muted">By {terminal.bus_line_name} bus cargo</span>
            </p>
          )}
          <p className="m-0 type-small text-text-muted">
            Collected by <span className="text-text-strong">{contact.recipient_name}</span>, <span className="type-sku">{contact.phone}</span>
          </p>
        </div>
      )}
    </Card>
  );
}

function Label({ children }: { children: ReactNode }) {
  return <p className="mb-2 type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">{children}</p>;
}
